/**
 * "Nachweise & Pflichten" on the tenant detail page.
 *
 * The same three obligations the global workspace lists, narrowed to one
 * person and grouped by the contract each one belongs to. Every value comes
 * from the S4 read model and every change goes through the S5 endpoints — this
 * tab is a second VIEW of that truth, never a second copy of it.
 *
 * WHY GROUPED RATHER THAN FLAT. A person can hold several tenancies at once —
 * an internal move overlaps two — and a flat list of nine rows would leave the
 * reader matching reference numbers by eye. The grouping is the one the
 * Mietverhältnisse tab already uses: the same `groupTenancies` buckets, the
 * same four labels, the same order. Requirement rows are assigned to a tenancy
 * by `row.tenancy_id`, so nothing here decides which contract a row belongs to.
 *
 * THE VIEWER IS NOT ALWAYS THE PRIMARY TENANT. `include_participant` brings in
 * tenancies this person only co-signs, and on those the liability requirement
 * still belongs to the tenancy's primary tenant. The table already says whose
 * policy it is (`evidence_tenant_name` from S4); the group header says what
 * this person is on that contract, through the same `viewerRoleLabel` the
 * Mietverhältnisse tab uses. Neither is worked out here.
 *
 * NO FILTERS, NO PAGING. The global page needs both because it lists an
 * organisation; a tenant has a handful of rows under headings that already
 * separate them. A filter bar here would be the workspace toolbar shrunk down,
 * answering a question nobody standing on one person's file is asking.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";
import {
  fetchAdminTenancyRequirements,
  unwaiveRequirement,
} from "../../../../api/adminData";
import RequirementDetailDrawer from "../../nachweise/RequirementDetailDrawer";
import RequirementDueDateModal from "../../nachweise/RequirementDueDateModal";
import RequirementWaiveModal from "../../nachweise/RequirementWaiveModal";
import RequirementsTable, { rowKey } from "../../nachweise/RequirementsTable";
import {
  TENANCY_GROUPS,
  TENANCY_GROUP_LABELS,
  groupTenancies,
  tenancyHeaderSegments,
  viewerRoleLabel,
} from "../tenancies/tenancyViewSelectors";

/**
 * One page, deliberately larger than the endpoint's default.
 *
 * Three rows per tenancy means 200 covers about 66 contracts for one person —
 * far beyond anything real — and it is the same ceiling the page already uses
 * for its tenancy list. It is the endpoint's maximum, not a number to raise:
 * a tenant who exceeded it would need paging, not a bigger single request.
 */
const PAGE_LIMIT = 200;

const PANEL = "rounded-2xl bg-background p-4 sm:p-5";

/**
 * Requirement rows, arranged into one block per tenancy.
 *
 * Driven by the ROWS, ordered by the tenancy list. Every row reaches the
 * screen: a block whose tenancy is in the list is bucketed and labelled, and
 * one whose tenancy is missing comes back as a loose row rather than being
 * silently dropped — an empty tab in front of a person who has obligations
 * would be the worst possible answer.
 *
 * The loose rows are returned FLAT, not as pseudo-blocks. A block exists to
 * carry a heading naming its contract, and for these rows there is no contract
 * record to name one from; inventing a heading out of the row would be this
 * component guessing at tenancy identity, which is the one thing it must not
 * do. They get the table's own context columns instead — see the render.
 */
function buildBlocks(items, tenancies) {
  const byTenancy = new Map();
  for (const row of items || []) {
    const key = String(row?.tenancy_id ?? "");
    if (!key) continue;
    if (!byTenancy.has(key)) byTenancy.set(key, []);
    byTenancy.get(key).push(row);
  }

  const grouped = groupTenancies(tenancies);
  const claimed = new Set();

  const groups = TENANCY_GROUPS.map((key) => {
    const blocks = [];
    for (const tenancy of grouped[key] || []) {
      const id = String(tenancy?.id ?? "");
      const rows = byTenancy.get(id);
      if (!rows || rows.length === 0) continue;
      claimed.add(id);
      blocks.push({ id, tenancy, rows });
    }
    return { key, label: TENANCY_GROUP_LABELS[key], blocks };
  }).filter((g) => g.blocks.length > 0);

  const orphanRows = [];
  for (const [id, rows] of byTenancy) {
    if (!claimed.has(id)) orphanRows.push(...rows);
  }

  return { groups, orphanRows };
}

/** Reference number, object line and the viewer's role — once per tenancy.
 *  Only ever rendered for a block whose tenancy record the page holds. */
function TenancyBlockHeader({ block, tenantId }) {
  const { tenancy, rows } = block;
  const reference =
    tenancy?.reference_number || rows[0]?.tenancy_reference_number || null;
  const objectLine = tenancyHeaderSegments(tenancy).join(" · ");
  const role = viewerRoleLabel(tenancy, tenantId);

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-[13px] font-medium text-foreground">
        {objectLine || "Mietverhältnis"}
      </span>
      {reference ? (
        <span
          className="text-[12px] text-muted-foreground"
          data-testid={`requirements-tenancy-reference-${block.id}`}
        >
          Referenz:{" "}
          <span className="font-mono text-[12px] text-foreground">{reference}</span>
        </span>
      ) : null}
      {role ? (
        <span
          className="inline-flex items-center whitespace-nowrap rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground"
          data-testid={`requirements-viewer-role-${block.id}`}
        >
          {role}
        </span>
      ) : null}
    </div>
  );
}

export default function TenantRequirementsTab({
  tenantId,
  tenancies,
  onActivityRefresh,
  onGoToTab,
}) {
  const [data, setData] = useState({
    loading: true,
    error: null,
    items: [],
    total: 0,
  });
  const [busyKey, setBusyKey] = useState(null);
  const [dueDateRow, setDueDateRow] = useState(null);
  const [waiveRow, setWaiveRow] = useState(null);
  const [waiveMode, setWaiveMode] = useState("waive");

  // The same identity-not-object rule the workspace page states: a reload after
  // a documented contact must reach the open drawer, and a captured row would
  // not receive it.
  const [selectedKey, setSelectedKey] = useState(null);

  // Newest request wins: a refetch triggered by a write must not be repainted
  // by an earlier one that happened to land later.
  const requestSeq = useRef(0);

  /**
   * The tenant's requirements, in one request.
   *
   * `includeParticipant` because the page already lists the tenancies this
   * person only co-signs, and a tab beside it showing fewer contracts would
   * read as a bug. `includeSuspended` because this is the whole file on one
   * person: the Mietverhältnisse tab shows their ended and cancelled
   * contracts, and hiding those requirements would make three rows vanish for
   * a tenancy the reader can see one tab over.
   */
  const load = useCallback(() => {
    if (!tenantId) return Promise.resolve();
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setData((prev) => ({ ...prev, loading: true, error: null }));

    return fetchAdminTenancyRequirements({
      tenantId,
      includeParticipant: true,
      includeSuspended: true,
      limit: PAGE_LIMIT,
    })
      .then((res) => {
        if (requestSeq.current !== seq) return;
        setData({
          loading: false,
          error: null,
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total || 0),
        });
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setData({
          loading: false,
          error: e?.message || "Nachweise konnten nicht geladen werden.",
          items: [],
          total: 0,
        });
      });
  }, [tenantId]);

  // The panel is lazy-mounted, so this runs when the tab is first opened —
  // never while the reader is on another one.
  useEffect(() => {
    load();
  }, [load]);

  const closeModals = useCallback(() => {
    setDueDateRow(null);
    setWaiveRow(null);
  }, []);

  /**
   * After a write that the server confirmed.
   *
   * The activity bump comes FIRST and unconditionally. The audit entry exists
   * the moment the write returned; making the feed's refresh wait on the
   * requirement reload would leave an open timeline stale whenever that second
   * request failed — for a change that did happen. A failing reload shows the
   * panel's own read error with a retry; it never reports the write as failed.
   */
  const onSaved = useCallback(() => {
    closeModals();
    if (typeof onActivityRefresh === "function") onActivityRefresh();
    load();
  }, [closeModals, onActivityRefresh, load]);

  const handleUnwaive = useCallback(
    async (row) => {
      setBusyKey(rowKey(row));
      try {
        await unwaiveRequirement(row.tenancy_id, row.requirement_type);
        toast.success("Erlass aufgehoben.");
        if (typeof onActivityRefresh === "function") onActivityRefresh();
        load();
      } catch (e) {
        toast.error(e?.message || "Der Erlass konnte nicht aufgehoben werden.");
      } finally {
        setBusyKey(null);
      }
    },
    [onActivityRefresh, load]
  );

  const actions = useMemo(
    () => ({
      onEditDueDate: (row) => setDueDateRow(row),
      onWaive: (row) => {
        setWaiveMode("waive");
        setWaiveRow(row);
      },
      onEditReason: (row) => {
        setWaiveMode("reason");
        setWaiveRow(row);
      },
      onUnwaive: handleUnwaive,
    }),
    [handleUnwaive]
  );

  const { groups, orphanRows } = useMemo(
    () => buildBlocks(data.items, tenancies),
    [data.items, tenancies]
  );

  const selectedRow = useMemo(
    () =>
      selectedKey ? data.items.find((r) => rowKey(r) === selectedKey) || null : null,
    [selectedKey, data.items]
  );

  const openDetails = useCallback((row) => setSelectedKey(rowKey(row)), []);
  const closeDetails = useCallback(() => setSelectedKey(null), []);

  /**
   * After a contact was documented here.
   *
   * Requirement rows only — deliberately NOT `onActivityRefresh`. A
   * communication writes no audit entry and no tenant event (S8x-1 records
   * that decision at the migration), so the activity feed has nothing new to
   * show; bumping it would be a request that can only return what it already
   * had. The due-date and waiver writes keep their bump, because those DO
   * write audit entries.
   */
  const onContactCreated = useCallback(() => {
    load();
  }, [load]);

  // The endpoint's ceiling was reached. Saying nothing here would present a
  // truncated list as the complete file.
  const truncated = !data.loading && !data.error && data.total > data.items.length;

  const hasTenancies = Array.isArray(tenancies) && tenancies.length > 0;

  function renderBlock(block) {
    return (
      <section
        key={block.id}
        className="flex flex-col gap-2"
        data-testid={`requirements-tenancy-${block.id}`}
      >
        <TenancyBlockHeader block={block} tenantId={tenantId} />
        <RequirementsTable
          rows={block.rows}
          actions={actions}
          busyKey={busyKey}
          contextMode="tenancyGroup"
          onOpenDetails={openDetails}
        />
      </section>
    );
  }

  return (
    <div className={PANEL}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-[15px] font-semibold tracking-tight text-foreground">
            Nachweise &amp; Pflichten
          </h2>
          <p className="m-0 mt-[3px] text-[12.5px] leading-snug text-muted-foreground">
            Wohnsitzanmeldung, Privathaftpflicht und Mietkaution je Mietverhältnis.
          </p>
        </div>
        {/* The documents themselves live in the Dokumente tab and stay there —
            this only takes the reader to it. Deliberately one control for the
            whole tab rather than one per row: the documents tab has no
            per-tenancy address to link to, and inventing one here would be a
            promise the other tab does not keep. */}
        {typeof onGoToTab === "function" ? (
          <button
            type="button"
            onClick={() => onGoToTab("documents")}
            className="shrink-0 rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Dokumente anzeigen
          </button>
        ) : null}
      </div>

      <div className="mt-4">
        {data.loading ? (
          <div
            className="flex flex-col gap-3"
            aria-busy="true"
            data-testid="requirements-tab-loading"
          >
            <span className="sr-only">Nachweise werden geladen …</span>
            <Skeleton className="h-[92px] w-full rounded-xl" />
            <Skeleton className="h-[92px] w-full rounded-xl" />
          </div>
        ) : data.error ? (
          <div
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/5 p-4"
            data-testid="requirements-tab-error"
          >
            <p className="m-0 text-[13px] font-medium text-destructive">
              Nachweise konnten nicht geladen werden.
            </p>
            <p className="m-0 mt-1 text-[12.5px] text-muted-foreground">
              {data.error}
            </p>
            <button
              type="button"
              onClick={load}
              className="mt-3 rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Erneut versuchen
            </button>
          </div>
        ) : data.items.length === 0 ? (
          <p
            className="m-0 text-[13px] text-muted-foreground"
            data-testid="requirements-tab-empty"
          >
            {hasTenancies
              ? "Keine Nachweise & Pflichten vorhanden."
              : "Keine Mietverhältnisse vorhanden."}
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {truncated ? (
              <p
                className="m-0 text-[12.5px] text-muted-foreground"
                data-testid="requirements-tab-truncated"
              >
                Es werden die ersten {data.items.length} von {data.total} Einträgen
                angezeigt.
              </p>
            ) : null}

            {groups.map((group) => (
              <section key={group.key} className="flex flex-col gap-3">
                <h3
                  className="m-0 text-[11px] font-semibold uppercase tracking-[0.7px] text-muted-foreground"
                  data-testid={`requirements-group-${group.key}`}
                >
                  {group.label} ({group.blocks.length})
                </h3>
                <div className="flex flex-col gap-5">
                  {group.blocks.map(renderBlock)}
                </div>
              </section>
            ))}

            {/* A degraded branch, and it says less rather than something wrong.
                These rows name a tenancy the page has no record of, so there
                is no heading to put above them and no role to state. The table
                therefore runs in its DEFAULT mode, where every row carries its
                own tenant, reference number and object — the columns the
                grouped blocks drop precisely because their heading supplies
                them. The tenant's name repeating down the column is the cost,
                and it is the right one: losing which contract a requirement
                belongs to would make the row unusable. */}
            {orphanRows.length > 0 ? (
              <section
                className="flex flex-col gap-3"
                data-testid="requirements-unmatched"
              >
                <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.7px] text-muted-foreground">
                  Weitere Nachweise ({orphanRows.length})
                </h3>
                <RequirementsTable
                  rows={orphanRows}
                  actions={actions}
                  busyKey={busyKey}
                  onOpenDetails={openDetails}
                />
              </section>
            ) : null}
          </div>
        )}
      </div>

      {/* The SAME drawer the workspace renders, with two presentation props.
          No tenant link — the reader is already on that person's page and the
          link would point at itself. A documents button, though, because this
          page holds the callback that switches tabs in place; the workspace has
          no such address and therefore offers nothing. */}
      <RequirementDetailDrawer
        row={selectedRow}
        onClose={closeDetails}
        onRequirementRefresh={onContactCreated}
        showTenantNavigation={false}
        onGoToDocuments={
          typeof onGoToTab === "function" ? () => onGoToTab("documents") : undefined
        }
        actions={actions}
      />

      <RequirementDueDateModal
        row={dueDateRow}
        onClose={closeModals}
        onSaved={onSaved}
      />
      <RequirementWaiveModal
        row={waiveRow}
        mode={waiveMode}
        onClose={closeModals}
        onSaved={onSaved}
      />
    </div>
  );
}
